import React, { useState } from "react";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Select,
  HStack,
  Spinner,
  Center,
  Text,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { useAdminBookings, Booking, useUpdateBookingStatus } from "api/admin";

export default function Bookings(): JSX.Element {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  
  const { data, isLoading, isError } = useAdminBookings({ page, limit: 10, status });
  const statusMut = useUpdateBookingStatus();
  const toast = useToast();

  const handleStatus = async (id: string, newStatus: string) => {
    try {
      await statusMut.mutateAsync({ id, status: newStatus });
      toast({ title: "Status updated", status: "success", isClosable: true });
    } catch (e) {
      toast({ title: "Update failed", status: "error", isClosable: true });
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p="20px" mb="20px">
        <HStack mb="20px" spacing={4}>
          <Select placeholder="All Statuses" value={status} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setStatus(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </Select>
        </HStack>

        {isLoading ? (
          <Center p="40px"><Spinner /></Center>
        ) : isError ? (
          <Center p="40px"><Text color="red.500">Failed to load data.</Text></Center>
        ) : !data?.data?.length ? (
          <Center p="40px"><Text>No data found.</Text></Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>User</Th>
                  <Th>Location</Th>
                  <Th>Dates</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data?.data?.map((b: Booking) => (
                  <Tr key={b.id}>
                    <Td>{b.id.substring(0, 8)}</Td>
                    <Td>{b.user?.fullName}</Td>
                    <Td>{b.location?.name}</Td>
                    <Td>{new Date(b.fromDate).toLocaleDateString()} - {new Date(b.toDate).toLocaleDateString()}</Td>
                    <Td>₦{(b.transaction?.amount ?? 0).toLocaleString()}</Td>
                    <Td><Badge>{b.status}</Badge></Td>
                    <Td>
                      <Select size="sm" value={b.status} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => handleStatus(b.id, e.target.value)}>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </Select>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Flex justify="space-between" mt="4">
              <Button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Text>Page {data?.page} of {(data?.totalPage || 0)}</Text>
              <Button disabled={page >= (data?.totalPage || 0)} onClick={() => setPage(p => p + 1)}>Next</Button>
            </Flex>
          </Box>
        )}
      </Card>
    </Box>
  );
}
